import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { UseQueryResult } from '@tanstack/react-query';
import type { AlertBaselineResponse } from '@/types/alertDetail';

interface BaselineComparisonSectionProps {
  query: UseQueryResult<AlertBaselineResponse, Error>;
}

/** True when the cohort_label carries the p90→p75 substitution suffix. */
function isCohortP90Substituted(label: string | null): boolean {
  if (!label) return false;
  return /p90 reported as p75|p90 not stored/i.test(label);
}

function formatNumber(v: number | null, digits = 1): string {
  if (v == null) return '—';
  return v.toFixed(digits);
}

function pctDelta(a: number | null, b: number | null): {
  text: string;
  tone: 'over' | 'under' | 'neutral';
} {
  if (a == null || b == null || b === 0) {
    return { text: '—', tone: 'neutral' };
  }
  const delta = ((a - b) / b) * 100;
  const sign = delta > 0 ? '+' : '';
  return {
    text: `${sign}${delta.toFixed(0)}%`,
    tone: delta > 5 ? 'over' : delta < -5 ? 'under' : 'neutral',
  };
}

/**
 * Section 4: vehicle 90-day vs peer-cohort vs OEM, with +/-% callouts.
 *
 * Per the architect blueprint the cohort uses p90 where available; when
 * ml.peer_cohort_baseline only stores up-to-p75 the backend reports p75
 * in the p90 slot and flags it via cohort_label. We surface that
 * substitution as a friendly caption ("p75 used (p90 not yet computed)")
 * rather than echoing the raw label suffix to investigators.
 */
export function BaselineComparisonSection({
  query,
}: BaselineComparisonSectionProps): JSX.Element {
  if (query.isLoading) {
    return (
      <section aria-labelledby="baseline-comparison">
        <h2 id="baseline-comparison" className="text-lg font-semibold">
          Baseline comparison
        </h2>
        <div className="mt-3 grid gap-4 md:grid-cols-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </section>
    );
  }

  if (query.isError) {
    return (
      <section aria-labelledby="baseline-comparison">
        <h2 id="baseline-comparison" className="text-lg font-semibold">
          Baseline comparison
        </h2>
        <p className="mt-2 text-sm text-destructive">
          Failed to load baseline: {query.error?.message ?? 'unknown error'}
        </p>
      </section>
    );
  }

  const data = query.data;
  if (!data) return <></>;

  const { asset, cohort, oem } = data;

  // "No baseline" = none of the three slices has a usable l/100km figure.
  const noData =
    asset.l_per_100km == null &&
    cohort.p50_l_per_100km == null &&
    cohort.p90_l_per_100km == null &&
    oem.expected_l_per_100km == null;

  if (noData) {
    return (
      <section aria-labelledby="baseline-comparison">
        <h2 id="baseline-comparison" className="text-lg font-semibold">
          Baseline comparison
        </h2>
        <p className="mt-2 rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          Baseline not yet available for this vehicle.
        </p>
      </section>
    );
  }

  const vsCohortP50 = pctDelta(asset.l_per_100km, cohort.p50_l_per_100km);
  const vsOem = pctDelta(asset.l_per_100km, oem.expected_l_per_100km);
  const cohortP90Substituted = isCohortP90Substituted(cohort.cohort_label);

  return (
    <section aria-labelledby="baseline-comparison">
      <h2 id="baseline-comparison" className="text-lg font-semibold">
        Baseline comparison
      </h2>
      <div className="mt-3 grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase text-muted-foreground">
              This vehicle (90d)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <div className="text-2xl font-semibold">
              {formatNumber(asset.l_per_100km)}{' '}
              <span className="text-xs text-muted-foreground">L/100km</span>
            </div>
            <div className="text-xs text-muted-foreground">
              avg fill: {formatNumber(asset.avg_fill_litres)} L · n=
              {asset.sample_size ?? '—'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase text-muted-foreground">
              Peer cohort
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <div className="text-2xl font-semibold">
              {formatNumber(cohort.p50_l_per_100km)}{' '}
              <span className="text-xs text-muted-foreground">
                L/100km (median)
              </span>
            </div>
            <div className="text-xs">
              p90:{' '}
              <span className="font-medium">
                {formatNumber(cohort.p90_l_per_100km)}
              </span>
            </div>
            {cohortP90Substituted && (
              <div className="text-[11px] italic text-muted-foreground">
                p75 used (p90 not yet computed)
              </div>
            )}
            <div className="text-xs text-muted-foreground">
              cohort size: {cohort.cohort_size ?? '—'}
            </div>
            <div
              className={cn(
                'mt-1 text-xs font-medium',
                vsCohortP50.tone === 'over' && 'text-red-600',
                vsCohortP50.tone === 'under' && 'text-emerald-600',
                vsCohortP50.tone === 'neutral' && 'text-muted-foreground',
              )}
            >
              vs median: {vsCohortP50.text}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase text-muted-foreground">
              OEM expected
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <div className="text-2xl font-semibold">
              {formatNumber(oem.expected_l_per_100km)}{' '}
              <span className="text-xs text-muted-foreground">L/100km</span>
            </div>
            <div className="text-xs text-muted-foreground">
              tank: {formatNumber(oem.tank_capacity_l, 0)} L · source:{' '}
              {oem.source ?? '—'}
            </div>
            <div
              className={cn(
                'mt-1 text-xs font-medium',
                vsOem.tone === 'over' && 'text-red-600',
                vsOem.tone === 'under' && 'text-emerald-600',
                vsOem.tone === 'neutral' && 'text-muted-foreground',
              )}
            >
              vs OEM: {vsOem.text}
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
